import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { formatHour } from '../../lib/format';

/**
 * Create Open Play session dialog. Multi-court selector, time range,
 * player cap, price, walk-in / auto-close toggles.
 * Extracted from OperatorToday.
 */
export default function CreateOpenPlayDialog({
  open,
  onOpenChange,
  courts,
  hours,
  date,
  selectedCourts,
  startHour,
  endHour,
  maxPlayers,
  price,
  allowWalkins,
  autoClose,
  onDateChange,
  onToggleCourt,          // (courtId) => void
  onStartHourChange,
  onEndHourChange,
  onMaxPlayersChange,
  onPriceChange,
  onAllowWalkinsChange,
  onAutoCloseChange,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-open-play-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Create Open Play Session</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Date */}
          <div>
            <Label className="text-sm font-medium text-text-secondary">Date</Label>
            <Input
              type="date"
              data-testid="op-date-input"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              className="rounded-xl mt-1.5 border-border-subtle focus:border-open-play focus:ring-open-play/20"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-text-secondary">Courts</Label>
            <div className="flex gap-2 flex-wrap mt-1.5">
              {courts.map(court => (
                <button
                  key={court.id}
                  data-testid={`op-court-${court.id}`}
                  onClick={() => onToggleCourt(court.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                    selectedCourts.includes(court.id)
                      ? 'bg-open-play text-white border-open-play'
                      : 'bg-white text-text-secondary border-border-subtle hover:border-open-play hover:text-open-play'
                  }`}
                >
                  {court.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-text-secondary">Start Hour</Label>
              <Select value={startHour} onValueChange={onStartHourChange}>
                <SelectTrigger className="rounded-xl mt-1.5 border-border-subtle" data-testid="op-start-select">
                  <SelectValue placeholder="Start" />
                </SelectTrigger>
                <SelectContent>
                  {hours.map(h => (
                    <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-text-secondary">End Hour</Label>
              <Select value={endHour} onValueChange={onEndHourChange}>
                <SelectTrigger className="rounded-xl mt-1.5 border-border-subtle" data-testid="op-end-select">
                  <SelectValue placeholder="End" />
                </SelectTrigger>
                <SelectContent>
                  {hours.filter(h => {
                    const start = parseInt(startHour);
                    return isNaN(start) || h >= start;
                  }).map(h => (
                    <SelectItem key={h} value={String(h + 1)}>{formatHour(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-text-secondary">Max Players</Label>
              <Input
                data-testid="op-max-players"
                value={maxPlayers}
                onChange={(e) => onMaxPlayersChange(e.target.value)}
                type="number"
                className="rounded-xl mt-1.5 border-border-subtle focus:border-open-play"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-text-secondary">Price per Player (₱)</Label>
              <Input
                data-testid="op-price"
                value={price}
                onChange={(e) => onPriceChange(e.target.value)}
                type="number"
                className="rounded-xl mt-1.5 border-border-subtle focus:border-open-play"
              />
            </div>
          </div>
          <div className="flex items-center justify-between py-1">
            <Label className="text-sm font-medium text-text-secondary">Allow Walk-ins</Label>
            <Switch
              data-testid="op-walkins-switch"
              checked={allowWalkins}
              onCheckedChange={onAllowWalkinsChange}
              className="data-[state=checked]:bg-open-play"
            />
          </div>
          <div className="flex items-center justify-between py-1">
            <Label className="text-sm font-medium text-text-secondary">Auto-close When Full</Label>
            <Switch
              data-testid="op-autoclose-switch"
              checked={autoClose}
              onCheckedChange={onAutoCloseChange}
              className="data-[state=checked]:bg-open-play"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="op-submit-btn"
            onClick={onSubmit}
            className="bg-open-play hover:bg-open-play/90 text-white rounded-xl px-8 py-5"
          >
            Create Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
